import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileDown } from 'lucide-react';
import { Button, useToast } from '../../ui';
import type { SprintReportData } from '../../features/sprintboard/report/sprintReportModel';
import { SprintReportDocument } from './SprintReportDocument';

/**
 * SprintReportButton — one-click "Download 1-page report" action for the sprint
 * header. It renders the {@link SprintReportDocument} off-screen (via a portal
 * on document.body so it survives the print fallback that hides #root) and, on
 * click, rasterizes it to a genuine single-page PDF with html2canvas + jspdf.
 *
 * The heavy libs are dynamically imported inside the handler so they are
 * code-split out of the main bundle. If the canvas/PDF path throws (e.g. an
 * older browser), it falls back to window.print() with the @media print rules
 * in index.css, which lay the same node out as one printed page.
 */

/** Slugifies a sprint name into a safe file stem. */
function fileStem(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'sprint';
}

/** Rasterizes a node and saves it as a single-page A4 portrait PDF. */
async function exportOnePagePdf(node: HTMLElement, filename: string): Promise<void> {
  const [jspdfMod, html2canvasMod] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);
  const { jsPDF } = jspdfMod;
  const html2canvas = html2canvasMod.default;

  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  });

  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  // Scale the whole capture to fit within one page (never spill to a 2nd page).
  const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    (pageW - w) / 2,
    0,
    w,
    h,
  );
  pdf.save(filename);
}

/** Browser-print fallback: hide the app, print the report node, then restore. */
function printFallback(): void {
  document.body.classList.add('ev-printing');
  const cleanup = () => {
    document.body.classList.remove('ev-printing');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // Give the class change a frame to apply before the print dialog opens.
  window.setTimeout(() => window.print(), 50);
}

export interface SprintReportButtonProps {
  data: SprintReportData;
  disabled?: boolean;
}

export function SprintReportButton({ data, disabled }: SprintReportButtonProps) {
  const { toast } = useToast();
  const reportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleDownload = useCallback(async () => {
    const node = reportRef.current;
    if (!node) return;
    setExporting(true);
    try {
      await exportOnePagePdf(node, `${fileStem(data.sprintName)}-report.pdf`);
    } catch {
      // PDF path unavailable — fall back to the browser's print dialog.
      try {
        printFallback();
        toast({
          tone: 'info',
          title: 'Opening print dialog',
          description:
            'PDF export was unavailable, so the one-page report opened in your browser’s print dialog instead — choose “Save as PDF”.',
        });
      } catch {
        toast({
          tone: 'error',
          title: 'Couldn’t generate the report',
          description: 'Please try again, or use your browser’s print to PDF.',
        });
      }
    } finally {
      setExporting(false);
    }
  }, [data.sprintName, toast]);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<FileDown className="h-4 w-4" />}
        loading={exporting}
        disabled={disabled}
        onClick={() => void handleDownload()}
      >
        Download 1-page report
      </Button>

      {createPortal(
        <div className="ev-report-offscreen" aria-hidden="true">
          <SprintReportDocument ref={reportRef} data={data} />
        </div>,
        document.body,
      )}
    </>
  );
}

export default SprintReportButton;
